// src/components/overview/SyncFlowDiagram.jsx
import React from 'react';

/* ── Pipeline step definitions ────────────────────────────── */

const PIPELINE_STEPS = [
  { key: 'courses',   label: 'Courses',   icon: 'bi-book',               typeMatches: ['course'] },
  { key: 'batches',   label: 'Batches',   icon: 'bi-people',             typeMatches: ['batch'] },
  { key: 'users',     label: 'Users',     icon: 'bi-people-fill',        typeMatches: ['users', 'users-sync'] },
  { key: 'chapters',  label: 'Chapters',  icon: 'bi-list-nested',        typeMatches: ['chapter'] },
  { key: 'content',   label: 'Content',   icon: 'bi-file-earmark-text',  typeMatches: ['content'] },
  { key: 'attempts',  label: 'Attempts',  icon: 'bi-clipboard-check',    typeMatches: ['attempts', 'attempt'] },
  { key: 'exams',     label: 'Exams',     icon: 'bi-journal-check',      typeMatches: ['exam'] },
  { key: 'mentors',   label: 'Mentors',   icon: 'bi-person-badge',       typeMatches: ['mentor'] },
];

/* ── Status detection ─────────────────────────────────────── */

/**
 * Returns 'active' | 'completed' | 'failed' | 'idle' for a given step
 * based on the array of currently running syncs.
 */
const getStepStatus = (step, runningSyncs) => {
  if (!runningSyncs || runningSyncs.length === 0) return 'idle';

  for (const sync of runningSyncs) {
    const type = sync.type || '';
    const currentStep = sync.currentStep || '';

    // Direct type match → active
    const typeMatch = step.typeMatches.some(
      (m) => type.includes(m) || currentStep.includes(m)
    );

    if (typeMatch) {
      if (sync.status === 'failed') return 'failed';
      return 'active';
    }

    // Check results for completed steps
    if (sync.results) {
      const resultKeys = Object.keys(sync.results);
      const hasResult = step.typeMatches.some((m) =>
        resultKeys.some((rk) => rk.toLowerCase().includes(m))
      );
      if (hasResult) return 'completed';
    }
  }

  return 'idle';
};

/* ── Status → style mapping ───────────────────────────────── */

const STATUS_STYLES = {
  idle: {
    color: 'var(--text-muted)',
    bg: 'transparent',
    border: 'var(--border)',
    label: 'idle',
  },
  active: {
    color: 'var(--accent-green)',
    bg: 'rgba(132, 204, 22, 0.08)',
    border: 'var(--accent-green)',
    label: 'active',
    pulse: true,
  },
  completed: {
    color: 'var(--accent-green)',
    bg: 'rgba(132, 204, 22, 0.05)',
    border: 'rgba(132, 204, 22, 0.4)',
    label: 'done',
  },
  failed: {
    color: 'var(--accent-red)',
    bg: 'rgba(239, 68, 68, 0.08)',
    border: 'var(--accent-red)',
    label: 'failed',
  },
};

/* ── Component ────────────────────────────────────────────── */

const SyncFlowDiagram = ({ runningSyncs }) => {
  const hasActiveSyncs = runningSyncs && runningSyncs.length > 0;

  return (
    <div className="theme-card flow-diagram-card">
      {/* Header */}
      <div className="flow-diagram-header">
        <i className="bi bi-diagram-3 me-2" style={{ color: 'var(--accent-green)' }} aria-hidden="true" />
        <span>Sync Pipeline</span>
        {!hasActiveSyncs && (
          <span className="flow-diagram-idle-badge">Idle</span>
        )}
      </div>

      {/* Pipeline nodes */}
      <div className="flow-diagram-nodes">
        {PIPELINE_STEPS.map((step, idx) => {
          const status = getStepStatus(step, runningSyncs);
          const styles = STATUS_STYLES[status];

          return (
            <React.Fragment key={step.key}>
              {/* Node */}
              <div
                className={`flow-node${styles.pulse ? ' flow-node--active' : ''}`}
                style={{
                  color: styles.color,
                  backgroundColor: styles.bg,
                  borderColor: styles.border,
                }}
                title={`${step.label}: ${styles.label}`}
              >
                <i className={`bi ${step.icon} flow-node-icon`} aria-hidden="true" />
                <span className="flow-node-label">{step.label}</span>
                <span
                  className="flow-node-status"
                  style={{ color: styles.color }}
                >
                  {styles.label}
                </span>
              </div>

              {/* Connector arrow between nodes (not after last) */}
              {idx < PIPELINE_STEPS.length - 1 && (
                <div className="flow-connector" aria-hidden="true">
                  <i className="bi bi-chevron-down flow-connector-icon" />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default SyncFlowDiagram;
